import { addMemberAmenitySlot } from "../../../../Redux/Bookings/BookingsActions";
import { getDashboardData } from "../../../../Redux/Dashboard/DashboardActions";
import validator, { ValidationField } from "../../../../Util/validator";
import toastHelper from "../../../../Util/toastHelper";
import { AppDispatch } from "../../../../Redux/store";
import styles from "./SlotSelectorModal.module.scss";
import Modal from "../../../Common/Modal/Modal";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { useState } from "react";
import moment from "moment";

interface Amenity {
  amenityId: number;
  name: string;
  image: string;
  isActive: boolean;
  lastVisited: string | null;
  amenityTypeName: string;
  isBookableSlot: boolean;
  maxhours: number;
  doors: AmenityDoor[];
  amenitySlots: AmenitySlot[];
}

interface AmenityDoor {
  id: number;
  doorName: string;
  doorId: number;
  panelId: number;
  amenityId: number;
}

interface AmenitySlot {
  amenityId: number;
  endDate: string;
  id: number;
  memberId: number;
  startDate: string;
}

interface Props {
  amenity: Amenity;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function SlotSelectorModal(props: Props) {
  const { amenity, isOpen, setIsOpen } = props;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [errors, setErrors] = useState<{ startTime: string; endTime: string }>({
    startTime: "",
    endTime: "",
  });

  const addSlotHandler = () => {
    let fieldsArray: ValidationField[] = [
      {
        key: "startTime",
        value: startTime,
        type: "text",
      },
      {
        key: "endTime",
        value: endTime,
        type: "text",
      },
    ];
    const { isValid, errors: fieldErrors } = validator(fieldsArray);
    setErrors({ ...errors, ...fieldErrors });
    if (isValid) {
      const start = moment(startTime, "HH:mm");
      const end = moment(endTime, "HH:mm");

      const diffMinutes = end.diff(start, "minutes");
      if (diffMinutes < 60) {
        toastHelper.error("Slot duration must be at least 1 hour");
        return;
      }
      if (diffMinutes > amenity.maxhours * 60) {
        toastHelper.error(
          `Slot duration must be less than ${amenity.maxhours} hours`,
        );
        return;
      }
      const todayDate = moment().format("YYYY-MM-DD");
      const successHandler = () => {
        setIsOpen(false);
        setStartTime("");
        setEndTime("");
        dispatch(getDashboardData({ navigate: navigate }));
      };
      dispatch(
        addMemberAmenitySlot({
          amenityId: amenity.amenityId.toString(),
          startDate: moment(
            `${todayDate} ${startTime}`,
            "YYYY-MM-DD HH:mm",
          ).format("YYYY-MM-DDTHH:mm:ss"),
          endDate: moment(`${todayDate} ${endTime}`, "YYYY-MM-DD HH:mm").format(
            "YYYY-MM-DDTHH:mm:ss",
          ),
          successHandler,
        }),
      );
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className={styles.mainContainer}>
        <div className={styles.headerContainer}>
          <div className={styles.headerTitle}>Select a slot</div>
        </div>
        <div className={styles.infoContentContainer}>
          <div className={styles.infoContainer}>
            <p className={styles.text}>
              The slot duration is 1 hour and the maximum duration is{" "}
              {amenity?.maxhours} hours
            </p>
          </div>
          <div className={styles.contentContainer}>
            <div className={styles.inputContainer}>
              <p className={styles.label}>Start Time</p>
              <input
                type="time"
                placeholder="Enter slot"
                className={styles.input}
                value={startTime}
                onChange={(e) => {
                  if (endTime) {
                    const endTimeData = moment(endTime, "HH:mm");
                    const startTimeData = moment(e.target.value, "HH:mm");
                    if (startTimeData.isSameOrAfter(endTimeData)) {
                      toastHelper.error("Start time must be before end time");
                      return;
                    }
                    setStartTime(e.target.value);
                    return;
                  }
                  if (
                    moment(e.target.value, "HH:mm").isBefore(moment(), "minute")
                  ) {
                    toastHelper.error("Start time must be in future");
                    return;
                  }
                  setStartTime(e.target.value);
                }}
              />
              <p className={styles.error}>{errors.startTime}</p>
            </div>
            <div className={styles.inputContainer}>
              <p className={styles.label}>End Time</p>
              <input
                type="time"
                placeholder="Enter slot"
                className={styles.input}
                value={endTime}
                onChange={(e) => {
                  if (startTime) {
                    const startTimeData = moment(startTime, "HH:mm");
                    const endTimeData = moment(e.target.value, "HH:mm");
                    if (endTimeData.isSameOrBefore(startTimeData)) {
                      toastHelper.error("End time must be after start time");
                      return;
                    }

                    if (
                      endTimeData.diff(startTimeData, "hours", true) >
                      amenity.maxhours
                    ) {
                      toastHelper.error("End time must be within the max hours");
                      return;
                    }
                    setEndTime(e.target.value);
                    return;
                  }
                  if (
                    moment(e.target.value, "HH:mm").isBefore(moment(), "minute")
                  ) {
                    toastHelper.error("End time must be in future");
                    return;
                  }
                  setEndTime(e.target.value);
                }}
              />
              <p className={styles.error}>{errors.endTime}</p>
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <button className={styles.button} onClick={addSlotHandler}>
              Add Slot
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default SlotSelectorModal;
function validateFields(fieldsArray: ValidationField[]): {
  isValid: any;
  errors: any;
} {
  throw new Error("Function not implemented.");
}
